import { createContext, useContext } from 'react';
import { FilterDefinition } from '../types';

/**
 * Stable values provided by ListView to its descendants.
 * All fields are memoized in the provider to avoid unnecessary re-renders.
 */
export interface ListViewContextValue {
  /** Unique id for the list (e.g. for React Query keys). */
  listId: string;

  /** Filter definitions. Empty array when no filters are configured. */
  filters: FilterDefinition[];

  /** Parsers map (filter name -> parser) for nuqs useQueryStates. Empty when no filters. */
  parsers: Record<string, any>;

  /**
   * Sets a filter value by key. Must be used by filter components to update the URL.
   * The ListView always bundles this with a page reset (pageIndex -> 0) so that
   * filter changes and pagination stay in sync.
   */
  setFilterValue: (key: string, value: any) => void;

  /**
   * Clears all filters and resets page to 0. Used by ResetFiltersButton.
   */
  resetFilters: () => void;
}

const ListViewContext = createContext<ListViewContextValue | null>(null);

/**
 * Returns the ListView context. Must be used within a ListView.
 * @throws Error when used outside a ListView
 */
export function useListViewContext(): ListViewContextValue {
  const ctx = useContext(ListViewContext);

  if (ctx == null) {
    throw new Error('useListViewContext must be used within a ListView');
  }

  return ctx;
}

export { ListViewContext };
